import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight } from 'lucide-react';
import type { InspirationTemplate } from '@shared/adTemplateMetadata';
import { selectHomeInspirationCards } from './inspiration-cards';
import { useIsMobile } from '@/hooks/use-mobile';
import { useCanvasStore } from './canvas-store';

/** A fresh category sample per page mount, stable while editing the brief. */
export function DiscoverSections({ onUseTemplate }: {onUseTemplate: (style: string, axis?: 'style' | 'angle') => void}) {
  const { data: templates = [] } = useQuery<InspirationTemplate[]>({ queryKey: ['/api/ad-templates'] });
  const openAdLibrary = useCanvasStore(s => s.openAdLibrary);
  const [seed] = useState(() => Math.floor(Math.random() * 0x100000000));
  const samples = useMemo(() => selectHomeInspirationCards(templates, seed), [templates, seed]);
  const columns = useIsMobile() ? 2 : 4;
  if (!samples.length) return null;
  const caption = (axis: string, label: string) => samples.some(c => c.axis !== axis && c.label.toLowerCase() === label.toLowerCase())
    ? `${axis === 'style' ? 'Style' : 'Angle'}: ${label}` : label;
  const card = ({ axis, label, template }: typeof samples[number]) => (
    <button key={`${axis}:${label.toLowerCase()}`} type="button" className="adoption-discover-card" data-testid={`discover-card-${template.id}`}
      aria-label={`Use ${caption(axis, label)} template`} onClick={() => onUseTemplate(label, axis)}>
      <img src={template.imageUrl} alt={template.name} loading="lazy" />
      <span className="adoption-discover-caption">{caption(axis, label)} <ArrowRight size={15} aria-hidden="true" /></span>
    </button>
  );
  // Each column keeps native image heights, with an equal number of cards.
  return (
    <section className="adoption-inspiration" aria-labelledby="inspiration-title">
      <div className="adoption-inspiration-header">
        <div>
          <h2 id="inspiration-title">A little inspiration.</h2>
          <p>Choose a template to fill your brief. Edit it, then generate.</p>
        </div>
        <button type="button" className="adoption-text-link" onClick={() => openAdLibrary([])}>
          Browse all inspiration <ArrowRight size={14} style={{ display: 'inline', marginLeft: 4 }} aria-hidden="true" />
        </button>
      </div>
      <div className="adoption-inspiration-grid">
        {Array.from({ length: columns }, (_, column) => (
          <div className="adoption-inspiration-column" key={column}>
            {samples.filter((_, index) => index % columns === column).map(card)}
          </div>
        ))}
      </div>
    </section>
  );
}
